import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ReorderIcon from '@mui/icons-material/Reorder';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CircleIcon from "@mui/icons-material/Circle";
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import Avatar from "@mui/material/Avatar";
import logo from '../img/logo.png';
import ArabicFlag from '../img/Arabic_flag.png';
import FrenchFlag from '../img/French_flag.png';
import EnglishFlag from '../img/English_flag.png';
import Divider from '@mui/material/Divider';
import { auth, db } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import SchoolIcon from '@mui/icons-material/School';
import CallIcon from '@mui/icons-material/Call';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import Badge from '@mui/material/Badge';
import ChatIcon from "@mui/icons-material/Chat";
import { getTeacherNotifications } from './TeacherNotifications';
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import DeleteIcon from '@mui/icons-material/Delete';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, deleteDoc, collection, getDocs, query, onSnapshot, where, updateDoc } from 'firebase/firestore';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import "../i18n";
import ListItemIcon from "@mui/material/ListItemIcon";
import { getRoleLabels, getStoredTutorType } from "../utils/roleTerminology";

const LogoImage = styled('img')(({ theme }) => ({
  width: '150px',
  height: 'auto',
}));

const LANGS = [
  { code: "ar", label: "العربية", flagImg: ArabicFlag },
  { code: "fr", label: "French", flagImg: FrenchFlag },
  { code: "en", label: "English", flagImg: EnglishFlag},
];

const menuItemSx = {
  width: "100%",
  overflow: "hidden",
  borderRadius: 2,
  alignItems: "center",
  gap: 1.25,
  px: 1.5,
  py: 1.25,
};

const menuItemTextSx = {
  fontSize: 13.5,
  fontWeight: 800,
  color: "#111827",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  lineHeight: 1.3,
};

const menuItemSubTextSx = {
  fontSize: 12.5,
  color: "#6b7280",
  mt: 0.25,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  lineHeight: 1.3,
};

export default function MenuAppBar() {
  const { t, i18n } = useTranslation();
  const role = getRoleLabels(t, getStoredTutorType());
  const [langAnchorEl, setLangAnchorEl] = useState(null);

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleSelectLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("darssy_lang", code);
    handleLangMenuClose();
  };

  const currentLang = LANGS.find((l) => l.code === (i18n.language || "ar")) || LANGS[0];

  const navigate = useNavigate();
  const [authState, setAuthState] = React.useState(true);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [teacherUnreadChats, setTeacherUnreadChats] = useState(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      const fetchedNotifications = await getTeacherNotifications();
      setNotifications(fetchedNotifications);
      setNotificationsCount(fetchedNotifications.length);
    };

    fetchNotifications();
  }, []);

  // ✅ Realtime unread chat count for teacher (for the bell badge)
  useEffect(() => {
    let unsubConvos = null;

    const authInstance = getAuth();

    const unsubAuth = onAuthStateChanged(authInstance, (user) => {
      // Not logged in
      if (!user) {
        setTeacherUnreadChats(0);
        if (unsubConvos) {
          unsubConvos();
          unsubConvos = null;
        }
        return;
      }

      // If auth fires again, reset old listener
      if (unsubConvos) {
        unsubConvos();
        unsubConvos = null;
      }

      const qConvos = query(
        collection(db, "conversations"),
        where("teacherId", "==", user.uid)
      );

      unsubConvos = onSnapshot(
        qConvos,
        (snap) => {
          let total = 0;
          snap.forEach((d) => {
            const data = d.data() || {};
            total += Number(data.unreadCountTeacher || 0);
          });
          setTeacherUnreadChats(total);
        },
        (err) => {
          console.error("Teacher navbar unread chat listener error:", err);
          setTeacherUnreadChats(0);
        }
      );
    });

    return () => {
      if (unsubConvos) unsubConvos();
      unsubAuth();
    };
  }, []);


  const handleDeleteNotification = async (notificationId) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
  
      if (!user) {
        console.error('User is not authenticated.');
        return;
      }
  
      // Get the teacher's document where uid matches
      const teacherQuery = query(collection(db, 'teachers'));
      const teacherSnapshot = await getDocs(teacherQuery);
  
      const teacherDoc = teacherSnapshot.docs.find((doc) => doc.data().uid === user.uid);
  
      if (!teacherDoc) {
        console.error('Teacher document not found.');
        return;
      }
  
      // Access the notifications sub-collection
      const notificationDocRef = doc(
        db,
        'teachers',
        teacherDoc.id,
        'notifications',
        notificationId
      );
  
      // Delete the notification from Firestore
      await deleteDoc(notificationDocRef);
  
      // Update the notifications state to remove the deleted notification
      setNotifications((prevNotifications) => {
        const updatedNotifications = prevNotifications.filter(
          (notification) => notification.id !== notificationId
        );
        setNotificationsCount(updatedNotifications.length); // Update the badge count
        return updatedNotifications;
      });
  
      console.log('Notification deleted successfully.');
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };
  

  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  const handleNotificationClick = async (notification) => {
    if (notification?.id) {
      await handleDeleteNotification(notification.id);
    }

    const { groupId, postId } = notification;
  
    // Navigate to the forum page with the groupId and postId
    navigate(`/teacher/class/${groupId}/forum?postId=${postId}`);
  
    handleNotificationsClose();
  
    // Wait until the page is loaded, then scroll to the post and clean the URL
    setTimeout(() => {
      const postElement = document.getElementById(`post-${postId}`);
      if (postElement) {
        postElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  
        // Remove the postId from the URL after scrolling
        const newUrl = `/teacher/class/${groupId}/forum`;
        window.history.replaceState(null, '', newUrl);
  
        // Ensure the post does not remain focused
        postElement.blur();
  
        // Add a flag to prevent re-scrolling
        sessionStorage.setItem('scrolledPostId', postId);
      }
    }, 500); 
  };

  const markAllTeacherChatsAsRead = async () => {
    try {
      const user = getAuth().currentUser;
      if (!user) return;

      const conversationsQuery = query(
        collection(db, "conversations"),
        where("teacherId", "==", user.uid)
      );

      const snapshot = await getDocs(conversationsQuery);
      const updates = [];

      snapshot.forEach((conversationDoc) => {
        const data = conversationDoc.data() || {};
        const unread = Number(data.unreadCountTeacher || 0);
        if (unread > 0) {
          updates.push(
            updateDoc(doc(db, "conversations", conversationDoc.id), {
              unreadCountTeacher: 0,
            })
          );
        }
      });

      await Promise.all(updates);
    } catch (error) {
      console.error("Error marking teacher chats as read:", error);
    }
  };
  
  const handleMessagesNotificationClick = async () => {
    await markAllTeacherChatsAsRead();
    navigate("/teacher/chat");
    handleNotificationsClose();
  };


  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsMenu = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth); 
      navigate('/teacher/login'); 
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      handleClose(); 
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#ffffff",
          color: "#000000",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
          <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            <LogoImage src={logo} alt="Logo" /> 
          </Typography>

          {authState && (
            <div>
              {/* Language switch */}
              <Tooltip title={currentLang.label} arrow>
                <IconButton
                  aria-label="language"
                  aria-controls="language-menu"
                  aria-haspopup="true"
                  onClick={handleLangMenuOpen}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "14px",
                    "&:hover": { bgcolor: "#f3f4f6" },
                    mr: 1,
                    border: "1px solid #eef2f7",
                    bgcolor: "#ffffff",
                    overflow: "hidden",
                    p: 1,
                  }}
                >
                <Box
                  component="img"
                  src={currentLang.flagImg}
                  alt={currentLang.label}
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "4px",
                    display: "block",
                    objectFit: "contain",
                    imageRendering: "-webkit-optimize-contrast",
                  }}
                />
                </IconButton>
              </Tooltip>

              <Menu
                id="language-menu"
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      width: 280,
                      borderRadius: 3,
                      overflow: "hidden",
                      mt: 1,
                      boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      direction: "rtl",
                    },
                  },
                }}
                MenuListProps={{ sx: { p: 0 } }}
              >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                    {t("Language")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                    {t("Choose the app language")}
                  </Typography>
                </Box>

                {/* Body */}
                <Box sx={{ p: 1 }}>
                  {LANGS.map((lng) => {
                    const selected = (i18n.language || "ar") === lng.code;

                    return (
                      <MenuItem
                        key={lng.code}
                        onClick={() => handleSelectLanguage(lng.code)}
                        sx={{
                          borderRadius: 2,
                          alignItems: "center",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.15,
                          "&:hover": { bgcolor: "#f9fafb" },
                          bgcolor: selected ? "rgba(79,70,229,0.08)" : "transparent",
                        }}
                      >
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <Box
                          component="img"
                          src={lng.flagImg}
                          alt={lng.label}
                          sx={{
                            width: 24,
                            height: 16,
                            borderRadius: "4px",
                            display: "block",
                            objectFit: "contain",
                            imageRendering: "-webkit-optimize-contrast",
                          }}
                        />
                      </ListItemIcon>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                            {lng.label}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.15 }}>
                            {lng.code.toUpperCase()}
                          </Typography>
                        </Box>

                        {selected && <CircleIcon sx={{ fontSize: 10, color: "#4f46e5" }} />}
                      </MenuItem>
                    );
                  })}
                </Box>
              </Menu>
              {/* Notification Bell Icon */}
              <IconButton
                aria-label="notifications"
                aria-controls="notification-menu"
                aria-haspopup="true"
                onClick={handleNotificationsMenu}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  "&:hover": { bgcolor: "#f3f4f6" },
                  mr: 1,
                }}
              >
                <Badge
                  badgeContent={notificationsCount + teacherUnreadChats}
                  color="error"
                  overlap="circular"
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontWeight: 800,
                      minWidth: 18,
                      height: 18,
                      fontSize: 11,
                      borderRadius: "999px",
                      border: "2px solid #ffffff",
                    },
                  }}
                >
                  <NotificationsNoneIcon sx={{ fontSize: 22, color: "#111827" }} />
                </Badge>
              </IconButton>

              {/* Notifications Dropdown Menu */}
              <Menu
                id="notification-menu"
                anchorEl={notificationsAnchorEl}
                open={Boolean(notificationsAnchorEl)}
                onClose={handleNotificationsClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      width: 360,
                      borderRadius: 3,
                      overflow: "hidden",
                      mt: 1,
                      boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      direction: "rtl",
                    },
                  },
                }}
                MenuListProps={{ sx: { p: 0 } }}
              >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                    {t("Notifications")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                    {(notificationsCount + teacherUnreadChats) > 0
                      ? t("You have {{count}} new notification(s)", { count: notificationsCount + teacherUnreadChats })
                      : t("No new notifications")}
                  </Typography>
                </Box>

                {/* Body */}
                {(teacherUnreadChats > 0 || notifications.length > 0) ? (
                  <Box sx={{ p: 1, maxHeight: 320, overflowY: "auto" }}>

                    {/* ✅ Messages notification (shows only if unread messages exist) */}
                    {teacherUnreadChats > 0 && (
                      <MenuItem
                        onClick={handleMessagesNotificationClick}
                        sx={{
                          borderRadius: 2,
                          alignItems: "flex-start",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                          mb: 0.5,
                          "&:hover": { bgcolor: "#f9fafb" },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "rgba(239,68,68,0.12)",
                            color: "#ef4444",
                            flexShrink: 0,
                          }}
                        >
                          <ChatIcon sx={{ fontSize: 20 }} />
                        </Avatar>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                              {t("New messages")}
                            </Typography>
                            <CircleIcon sx={{ fontSize: 8, color: "#ef4444" }} />
                          </Box>

                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#4b5563",
                              mt: 0.25,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: 1.4,
                            }}
                          >
                            {t("You have {{count}} unread message(s). Click to open chats.", { count: teacherUnreadChats })}
                          </Typography>

                          <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                           {t("Click to view messages")}
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}

                    {/* Existing forum notifications */}
                    {notifications.map((notification) => (
                      <MenuItem
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          borderRadius: 2,
                          alignItems: "flex-start",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                          "&:hover": { bgcolor: "#f9fafb" },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "rgba(79,70,229,0.12)",
                            color: "#4f46e5",
                            flexShrink: 0,
                          }}
                        >
                          <QuestionAnswerIcon sx={{ fontSize: 20 }} />
                        </Avatar>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                             {t("New forum question")}
                            </Typography>
                            <CircleIcon sx={{ fontSize: 8, color: "#ef4444" }} />
                          </Box>

                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#4b5563",
                              mt: 0.25,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: 1.4,
                            }}
                          >
                            {t("{{name}} posted a question in the forum.", { name: notification.full_name })}
                          </Typography>

                          <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                           {t("Click to view details")}
                          </Typography>
                        </Box>

                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotification(notification.id);
                          }}
                          sx={{ color: "#ef4444", mt: 0.2 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </MenuItem>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 2.5, textAlign: "center" }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        mx: "auto",
                        bgcolor: "#f3f4f6",
                        color: "#6b7280",
                      }}
                    >
                      <NotificationsNoneIcon />
                    </Avatar>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, mt: 1, color: "#111827" }}>
                      {t("No notifications")}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.5 }}>
                      {t("When new notifications arrive, they will appear here.")}
                    </Typography>
                  </Box>
                )}
              </Menu>


              <IconButton
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  "&:hover": { bgcolor: "#f3f4f6" },
                }}
              >
                <ReorderIcon sx={{ fontSize: 22, color: "#111827" }} />
              </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{
                    paper: {
                      sx: {
                        width: 320,
                        borderRadius: 3,
                        overflow: "hidden",
                        mt: 1,
                        boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        direction: "rtl",
                      },
                    },
                  }}
                  MenuListProps={{ sx: { p: 0 } }}
                >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                    {t("Menu")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                    {t("Choose the page you want to open")}
                  </Typography>
                </Box>
                <Box sx={{ p: 1 }}>
                  <MenuItem
                    onClick={() => handleNavigate("/teacher/classes")}
                    sx={{
                      ...menuItemSx,
                      "&:hover": { bgcolor: "#f9fafb" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "rgba(79,70,229,0.12)",
                        color: "#4f46e5",
                        flexShrink: 0,
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 20 }} />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={menuItemTextSx}>
                        {t("Home page")}
                      </Typography>
                      <Typography sx={menuItemSubTextSx}>
                        {role.isCoach ? t("View and manage your programs") : t("View and manage your classes")}
                      </Typography>
                    </Box>
                  </MenuItem>


                  <MenuItem
                    onClick={() => handleNavigate("/teacher/students")}
                    sx={{
                      ...menuItemSx,
                      "&:hover": { bgcolor: "#f9fafb" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "rgba(16,185,129,0.12)",
                        color: "#10b981",
                        flexShrink: 0,
                      }}
                    >
                      <PersonAddAltOutlinedIcon sx={{ fontSize: 20 }} />
                    </Avatar> 

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={menuItemTextSx}>
                        {role.isCoach ? t("Clients page") : t("Students page")}
                      </Typography>
                      <Typography sx={menuItemSubTextSx}>
                        {role.isCoach ? t("View and add clients") : t("View and add students")}
                      </Typography>
                    </Box>
                  </MenuItem>

                  {!role.isCoach && (
                    <MenuItem
                      onClick={() => handleNavigate("/teacher/groups")}
                      sx={{
                        ...menuItemSx,
                        "&:hover": { bgcolor: "#f9fafb" },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "rgba(245,158,11,0.12)",
                          color: "#f59e0b",
                          flexShrink: 0,
                        }}
                      >
                        <GroupsOutlinedIcon sx={{ fontSize: 20 }} /> 
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={menuItemTextSx}>
                          {t("Groups page")}
                        </Typography>
                        <Typography sx={menuItemSubTextSx}>
                          {t("Create a new group and add students")}
                        </Typography>
                      </Box>
                    </MenuItem>
                  )}

                  <MenuItem
                    onClick={() => handleNavigate("/teacher/services")}
                    sx={{
                      ...menuItemSx,
                      "&:hover": { bgcolor: "#f9fafb" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "rgba(37,99,235,0.12)",
                        color: "#2563eb",
                        flexShrink: 0,
                      }}
                    >
                      <SellOutlinedIcon sx={{ fontSize: 20 }} />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={menuItemTextSx}>
                        {t("Services & Pricing")}
                      </Typography>
                      <Typography sx={menuItemSubTextSx}>
                        {t("Create services and define regional pricing")}
                      </Typography>
                    </Box>
                  </MenuItem>

                  <MenuItem
                    onClick={() => handleNavigate("/teacher/contactUs")}
                    sx={{
                      ...menuItemSx,
                      "&:hover": { bgcolor: "#f9fafb" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "rgba(16,185,129,0.12)",
                        color: "#10b981",
                        flexShrink: 0,
                      }}
                    >
                      <CallIcon sx={{ fontSize: 20 }} />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={menuItemTextSx}>
                        {t("Contact admin")}
                      </Typography>
                      <Typography sx={menuItemSubTextSx}>
                        {t("Send a message or complaint to the admin")}
                      </Typography>
                    </Box>
                  </MenuItem>


                  <Divider sx={{ my: 1, borderColor: "#eef2f7" }} />

                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      ...menuItemSx,
                      "&:hover": { bgcolor: "#fef2f2" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "rgba(239,68,68,0.12)",
                        color: "#ef4444",
                        flexShrink: 0,
                      }}
                    >
                      <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 20 }} />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={menuItemTextSx}>
                        {t("Log out")}
                      </Typography>
                      <Typography sx={menuItemSubTextSx}>
                        {t("End the current session")}
                      </Typography>
                    </Box>
                  </MenuItem>
                </Box>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
}
